import { achieveBusinessCountForTargetYearMonth, totalBusinessResult } from '@/coreDomainsModule/businessTotal';
import { IYearMonth } from '@/coreDomainsModule/date';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IUserService } from '@/coreDomainsModule/services/userService';
import { IBusinessProgressRateInfo } from '@/corePagesModule/progressRateTableContent/domains/entities/businessProgressRateInfo';
import { simpleCopy } from '@/utils/deepCopy';

/**
 * 進捗率情報の作成を行う
 */
export class ProgressRateInfoService {
  /**
   * 担当者別
   * @param businesses - 取引情報一覧
   * @param targetYearMonth - 選択年月情報
   */
  static createBusinessProgressRateInfoByUserName = async (
    businesses: IBusiness[],
    targetYearMonth: IYearMonth
  ): Promise<IBusinessProgressRateInfo[]> => {
    if (businesses.length === 0) return [];
    const aggregateBusiness = businesses.filter((ent) => ent.aggregateGroup && ent.aggregateGroup.length > 0);
    const output: IBusinessProgressRateInfo[] = [];
    const userList = await getListOfItemForFilter('userName');
    // 担当者別で進捗状況管理用の情報を作成する
    for (const userName of userList) {
      // 担当者が割り振られている集計行取引を抽出する
      const filteredBusiness = aggregateBusiness.filter(
        (business: IBusiness) =>
          business.aggregateBusinessResponsibleUsers && business.aggregateBusinessResponsibleUsers.some((user) => user.name === userName)
      );
      // 該当取引ID配列を作成
      const businessIds = filteredBusiness.map((ent) => ent.businessId);
      // 進捗状況管理用の情報を作成する
      const outputProgressRateInfo: IBusinessProgressRateInfo = {
        userName: userName,
        customerName: '',
        supplierName: '',
        hashTagInfo: [], // 一旦空
        totalBusinessCount: await totalBusinessResult(filteredBusiness),
        updatedBusinessCount: await achieveBusinessCountForTargetYearMonth(filteredBusiness, targetYearMonth),
        businessIds: businessIds,
        latestUpdateAt: 0,
      };
      // 出力用のオブジェクトに追加
      output.push(outputProgressRateInfo);
    }
    return output;
  };

  /**
   * 仕入先名別
   * @param businesses - 取引情報一覧
   * @param targetYearMonth - 選択年月情報
   */
  static createBusinessProgressRateInfoBySupplierName = async (
    businesses: IBusiness[],
    targetYearMonth: IYearMonth
  ): Promise<IBusinessProgressRateInfo[]> => {
    if (businesses.length === 0) return [];
    const aggregateBusiness = businesses.filter((ent) => ent.aggregateGroup && ent.aggregateGroup.length > 0);
    const output: IBusinessProgressRateInfo[] = [];
    const supplierNameList = await getListOfItemForFilter('supplierName', aggregateBusiness);
    // 仕入先名別で進捗状況管理用の情報を作成する
    for (const supplierName of supplierNameList) {
      // 仕入先名が割り振られている集計行取引を抽出する
      const filteredBusiness = aggregateBusiness.filter(
        (business: IBusiness) => business.supplierName !== undefined && business.supplierName === supplierName
      );
      // 該当取引ID配列を作成
      const businessIds = filteredBusiness.map((ent) => ent.businessId);
      // 進捗状況管理用の情報を作成する
      const outputProgressRateInfo: IBusinessProgressRateInfo = {
        userName: '',
        customerName: '',
        supplierName: supplierName,
        hashTagInfo: [], // 一旦空
        totalBusinessCount: await totalBusinessResult(filteredBusiness),
        updatedBusinessCount: await achieveBusinessCountForTargetYearMonth(filteredBusiness, targetYearMonth),
        businessIds: businessIds,
        latestUpdateAt: 0,
      };
      // 出力用のオブジェクトに追加
      output.push(outputProgressRateInfo);
    }
    return output;
  };

  /**
   * 売上先名別
   * @param businesses - 取引情報一覧
   * @param targetYearMonth - 選択年月情報
   */
  static createBusinessProgressRateInfoByCustomerName = async (
    businesses: IBusiness[],
    targetYearMonth: IYearMonth
  ): Promise<IBusinessProgressRateInfo[]> => {
    if (businesses.length === 0) return [];
    const aggregateBusiness = businesses.filter((ent) => ent.aggregateGroup && ent.aggregateGroup.length > 0);
    const childBusiness = expandAggregateBusinessList(aggregateBusiness);
    const output: IBusinessProgressRateInfo[] = [];
    const customerNameList = await getListOfItemForFilter('customerName', childBusiness);
    // 売上先名別で進捗状況管理用の情報を作成する
    for (const customerName of customerNameList) {
      // 売上先名が割り振られている集計行取引を抽出する
      const filteredBusiness = childBusiness.filter(
        (business: IBusiness) => business.customerName !== undefined && business.customerName === customerName
      );
      // 該当取引ID配列を作成
      const businessIds = filteredBusiness.map((ent) => ent.businessId);
      // 進捗状況管理用の情報を作成する
      const outputProgressRateInfo: IBusinessProgressRateInfo = {
        userName: '',
        customerName: customerName,
        supplierName: '',
        hashTagInfo: [], // 一旦空
        totalBusinessCount: await totalBusinessResult(filteredBusiness),
        updatedBusinessCount: await achieveBusinessCountForTargetYearMonth(filteredBusiness, targetYearMonth),
        businessIds: businessIds,
        latestUpdateAt: 0,
      };
      // 出力用のオブジェクトに追加
      output.push(outputProgressRateInfo);
    }
    return output;
  };

  /**
   * 担当者別の進捗状況管理用の情報を作成する 仕入秋ない
   * @param businesses - 取引情報一覧
   * @param businessList - 取引IDの配列
   * @param userNameList - 担当者名の配列
   * @param targetYearMonth - 内示作成年月
   */
  static createBusinessProgressRateInfoForSupplierName = async (
    businesses: IBusiness[],
    businessList: string[],
    userNameList: string[],
    targetYearMonth: IYearMonth
  ): Promise<IBusinessProgressRateInfo[]> => {
    if (businesses.length === 0) return [];
    const relatedBusinesses = expandAggregateBusinessList(businesses); // 関連取引情報のみをまとめた取引情報一覧
    const output: IBusinessProgressRateInfo[] = []; // 出力用のオブジェクト

    // 担当者名でフィルタリング
    const filteredUserBusiness = relatedBusinesses.filter((ent) => {
      if (userNameList.length === 0) return true; // 中身をそのまま流す
      return (
        ent.aggregateBusinessResponsibleUsers &&
        ent.aggregateBusinessResponsibleUsers.some((agUser) => agUser.name && userNameList.includes(agUser.name))
      );
    }); // 担当者が振られている集計行取引

    // 仕入先名一覧
    const supplierNameArray = Array.from(new Set(filteredUserBusiness.map((ent) => ent.supplierName)));

    // 担当者別で進捗状況管理用の情報を作成する
    for (const supplierName of supplierNameArray) {
      if (!supplierName) continue; // 仕入先名が空ならスキップ
      // 指定した仕入先名を持つ集計行取引を抽出
      const filteredSupplierBusiness = filteredUserBusiness.filter((ent) => ent.supplierName && ent.supplierName === supplierName); // 担当者が振られている集計行取引

      // 取引数
      const filteredSupplierBusinessCount = filteredSupplierBusiness.length;
      const supplierTest = filteredSupplierBusiness.filter((ent) => ent.supplierAnnounce);
      const achieveFilteredSupplierBusinessCount = filteredSupplierBusiness.filter((ent) => {
        return (
          ent.supplierAnnounce &&
          ent.supplierAnnounce.some(
            (supplierAnnounce) => supplierAnnounce.manageInitiatedYearMonth && supplierAnnounce.manageInitiatedYearMonth === targetYearMonth
          )
        );
      }).length;

      // 該当取引ID配列を作成
      const businessIds = filteredSupplierBusiness.map((ent) => ent.businessId);
      // 進捗状況管理用の情報を作成する
      const outputProgressRateInfo: IBusinessProgressRateInfo = {
        userName: '',
        customerName: '',
        supplierName: supplierName,
        hashTagInfo: [], // 一旦空
        totalBusinessCount: filteredSupplierBusinessCount,
        updatedBusinessCount: achieveFilteredSupplierBusinessCount,
        businessIds: businessIds,
        latestUpdateAt: 0,
      };
      // 出力用のオブジェクトに追加
      output.push(outputProgressRateInfo);
    }
    return output;
  };

  /**
   * 担当者別の進捗状況管理用の情報を作成する
   * @param businesses - 取引情報一覧
   * @param businessList - 取引IDの配列
   * @param userNameList - 担当者名の配列
   * @param customerNameList - 売上先名の配列
   * @param tagList - キーワード名(タグ情報)の配列
   * @param targetYearMonth - 内示作成年月
   */
  static createBusinessProgressRateInfoForAll = async (
    businesses: IBusiness[],
    businessList: string[],
    userNameList: string[],
    customerNameList: string[],
    tagList: string[],
    targetYearMonth: IYearMonth
  ): Promise<IBusinessProgressRateInfo[]> => {
    if (businesses.length === 0) return [];
    // const aggregateBusiness = businesses.filter((ent) => ent.aggregateGroup && ent.aggregateGroup.length > 0);
    const output: IBusinessProgressRateInfo[] = [];
    // const filteredBusinesses = aggregateBusiness.filter((ent) => {
    //   return businessList.some((businessId) => businessId === ent.businessId);
    // });
    const filteredBusinesses = expandAggregateBusinessList(businesses);
    // 担当者別で進捗状況管理用の情報を作成する
    // 指定した仕入先名を持つ集計行取引を抽出
    const filteredUserBusiness = filteredBusinesses.filter((ent) => {
      if (userNameList.length === 0) return true;
      return (
        ent.aggregateBusinessResponsibleUsers &&
        ent.aggregateBusinessResponsibleUsers.some((aguser) => aguser.name && userNameList.includes(aguser.name))
      );
    }); // 担当者が振られている集計行取引

    const filteredCustomerBusiness = filteredUserBusiness.filter((ent) => {
      if (customerNameList.length === 0) return true;
      return customerNameList.some((customerName) => ent.customerName && customerName === ent.customerName);
    }); // 担当者が振られている集計行取引

    const filteredTagBusiness = filteredCustomerBusiness.filter((ent) => {
      if (tagList.length === 0) return true;
      return tagList.some((supplierName) => ent.supplierName && supplierName === ent.supplierName);
    }); // 担当者が振られている集計行取引

    // const supplierNameArray = Array.from(new Set(filteredBusiness.map((ent) => ent.supplierName)));

    if (filteredTagBusiness.length > 0) {
      // 該当取引ID配列を作成
      const businessIds = filteredTagBusiness.map((ent) => ent.businessId);
      // 進捗状況管理用の情報を作成する
      const outputProgressRateInfo: IBusinessProgressRateInfo = {
        userName: '',
        customerName: '',
        supplierName: '',
        hashTagInfo: [], // 一旦空
        totalBusinessCount: await totalBusinessResult(filteredTagBusiness),
        updatedBusinessCount: await achieveBusinessCountForTargetYearMonth(filteredTagBusiness, targetYearMonth),
        businessIds: businessIds,
        latestUpdateAt: 0,
      };
      // 出力用のオブジェクトに追加
      output.push(outputProgressRateInfo);
    }
    return output;
  };
}

/**
 * フィルターするために必要なリストを取得する
 * @param key - なんの項目のリストを作成するか
 * @param business - 取引情報一覧
 */
const getListOfItemForFilter = async (key: 'userName' | 'supplierName' | 'customerName', business?: IBusiness[]): Promise<string[]> => {
  // 取引情報がない場合、userNameをkeyとする
  if (!business) key = 'userName';
  if (key === 'userName') {
    return await IUserService.getOrganizationUserEmail();
  } else if (key === 'supplierName') {
    if (!business) return [];
    // 仕入先名一覧の取得
    const supplierNameList = business
      ?.map((business: IBusiness) => business.supplierName)
      .filter((supplierName): supplierName is Exclude<typeof supplierName, undefined> => supplierName !== undefined);
    return Array.from(new Set(...supplierNameList));
  } else if (key === 'customerName') {
    if (!business) return [];
    // 仕入先名一覧の取得
    const customerNameList = business
      ?.map((business: IBusiness) => business.customerName)
      .filter((customerName): customerName is Exclude<typeof customerName, undefined> => customerName !== undefined);
    return Array.from(new Set(...customerNameList));
  }
  return [];
};

/**
 * 集計行取引が持っている関連取引情報を展開して、関連取引リストを作成する
 * TODO: 未転記取引が混ざっているので、関連取引のみを引っ張る処理を作成しているが、未転記取引取り除けたら外して
 * @param businesses - 取引情報一覧
 */
const expandAggregateBusinessList = (businesses: IBusiness[]): IBusiness[] => {
  // 取引情報が空
  if (!businesses.length) return [];

  const businessArray = simpleCopy(businesses); // 取引情報一覧
  const outputBusinessArray: IBusiness[] = []; // 出力用オブジェクト
  const filteredBusinesses = businessArray.filter((ent) => ent.aggregateGroup); // 集計行取引のみ抽出

  // 関連取引の展開
  filteredBusinesses.forEach((aggregateBusiness) => {
    // 集計行取引に関連取引が登録されているか？
    if (aggregateBusiness.aggregateGroup) {
      const relatedBusiness = businessArray.filter((ent) => aggregateBusiness.aggregateGroup?.includes(ent.businessId)); // 集計行が持つ関連取引情報
      outputBusinessArray.push(...relatedBusiness);
    }
  });
  return outputBusinessArray;
};
